import * as React from "react";
import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import { Field, Form } from "react-final-form";
import Asterisk from "@/shared/globals/UiElements/Asterisk";
import { useStore } from "@/lib/storeData/StoreContext";
import { PrimaryButton } from "@/components/Button";
import { PhoneIcon } from "@/assets/Icons/PhoneIcon";
import { MailIcon } from "@/assets/Icons/MailIcon";
import { AddressIcon } from "@/assets/Icons/AddressIcon";
import { FlexRow, Input, Label, TextArea } from "@/shared/globals";
import {
  Container,
  StyledSection,
  ContactForm,
  RequiredSpan,
  ContactInfo,
  AddressBook,
  InfoHolder,
  Info,
  HorizontalLine,
  TextFollow,
  ContactUsSubHeader,
  ContactInfoWrapper,
  ContactInfoHeader,
  ContactInfoValue,
  SocialLinksContainer,
} from "./styled";
import { SocialLinks } from "@/components/UtilityComponents/SocialLinks";
import { useSubmitStoreContactFormMutation } from "@/generated/graphql";
import InputPhone, { PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import { fireSubmitContactFormEvent } from "./utils";
import { FormApi } from "final-form";
import { CheckmarkCircleIcon } from "@/assets/Icons/CheckmarkCircleIcon";
import { AlertOctagonIcon } from "@/assets/Icons/AlertOctagonIcon";

export type ContactFormValues = {
  name: string;
  email: string;
  phone: PhoneInput;
  subjectTitle: string;
  subjectBody: string;
};

const SuccessMessage = () => (
  <div className="flex items-center bg-success-50 rounded-lg border border-success-200 p-2 gap-2xs">
    <CheckmarkCircleIcon />
    <span className="text-text-xs font-medium text-success-800">
      <FormattedMessage
        defaultMessage="We have received your message and will reach you as soon as possible."
        id="CxNsYA"
      />
    </span>
  </div>
);

const FailureMessage = () => (
  <div className="flex items-center bg-error-50 rounded-lg border border-error-200 p-2 gap-2xs">
    <AlertOctagonIcon />
    <span className="text-text-xs font-medium text-error-800">
      <FormattedMessage
        defaultMessage="Something went wrong, please try again."
        id="8GvVWZ"
      />
    </span>
  </div>
);

const Contact = () => {
  const intl = useIntl();
  const { storeId, socialMedia, contactInfo, location } = useStore();
  const [isSuccess, setIsSuccess] = React.useState<boolean | undefined>(
    undefined
  );

  const [SubmitStoreContactForm] = useSubmitStoreContactFormMutation();

  const email = contactInfo?.email;
  const phone = contactInfo?.phone;
  const address = location?.address;

  const handleSubmitContact = async (
    input: ContactFormValues,
    form: FormApi<ContactFormValues>
  ) => {
    try {
      const { data } = await SubmitStoreContactForm({
        variables: {
          input: {
            storeId,
            name: input.name,
            fromEmail: input.email,
            phone: input?.phone?.value!,
            messageSubject: input.subjectTitle,
            messageBody: input.subjectBody,
          },
        },
      });

      if (data?.submitStoreContactForm?.statusCode! < 400) {
        handleFeedback(true);
        fireSubmitContactFormEvent(input);
        form.reset();
        form.resetFieldState("name");
        form.resetFieldState("email");
        form.resetFieldState("phone");
        form.resetFieldState("subjectTitle");
        form.resetFieldState("subjectBody");
      } else {
        handleFeedback(false);
      }
    } catch (error) {
      handleFeedback(false);
    }
  };

  const handleFeedback = (value: boolean) => {
    setIsSuccess(value);
  };

  const isContactInfoExist = !!(email || phone || address);
  const socialLinkContent = socialMedia?.length ? (
    <SocialLinksContainer fullWidth>
      {isContactInfoExist && <HorizontalLine />}
      <TextFollow>
        <FormattedMessage
          defaultMessage="You also follow & contact us through"
          id="13yn26"
        />
      </TextFollow>
      <SocialLinks justifyContent="center" socialSettings={socialMedia} />
    </SocialLinksContainer>
  ) : null;

  return (
    <Container>
      <ContactUsSubHeader>
        <FormattedMessage
          defaultMessage="You can send your questions by filling out the form below & We will get back to you as soon as possible."
          id="STHKir"
        />
      </ContactUsSubHeader>
      <StyledSection
        fullWidth
        alignItems="flex-start"
        isColumn={!isContactInfoExist}
      >
        <ContactForm>
          <Form<ContactFormValues>
            onSubmit={(values, form) => handleSubmitContact(values, form)}
            validate={validate}
            initialValues={{}}
            render={({ handleSubmit, submitting, values, errors }) => {
              return (
                <form className="w-full" onSubmit={handleSubmit}>
                  <Label>
                    <FlexRow>
                      <FormattedMessage
                        defaultMessage="Full Name"
                        id="TemVby"
                      />
                      <Asterisk />
                    </FlexRow>
                    <Field name="name">
                      {({ input, meta: { error, touched } }) => (
                        <>
                          <Input
                            {...input}
                            className={error && touched ? "invalid" : ""}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Enter your Name",
                              id: "hETAx0",
                            })}
                            required
                          />
                          {error && touched && (
                            <RequiredSpan>{error}</RequiredSpan>
                          )}
                        </>
                      )}
                    </Field>
                  </Label>
                  <Label>
                    <FlexRow>
                      <FormattedMessage
                        defaultMessage="Your E-mail"
                        id="zYYgGl"
                      />
                      <Asterisk />
                    </FlexRow>
                    <Field name="email">
                      {({ input, meta: { error, touched } }) => (
                        <>
                          <Input
                            {...input}
                            className={error && touched ? "invalid" : ""}
                            type="email"
                            placeholder={intl.formatMessage({
                              defaultMessage: "Enter your Email",
                              id: "7DVdbY",
                            })}
                            required
                          />
                          {error && touched && (
                            <RequiredSpan>{error}</RequiredSpan>
                          )}
                        </>
                      )}
                    </Field>
                  </Label>
                  <Label>
                    <FlexRow>
                      <FormattedMessage
                        defaultMessage="Phone no."
                        id="1SphRN"
                      />
                      <Asterisk />
                    </FlexRow>
                    <Field<PhoneInput> name="phone">
                      {(fieldProps) => (
                        <InputPhone
                          {...fieldProps}
                          dataTest="type-primary-phone"
                          placeholder={intl.formatMessage({
                            defaultMessage: "Enter your Phone Number",
                            id: "in8gTr",
                          })}
                          required
                        />
                      )}
                    </Field>
                  </Label>
                  <Label>
                    <FlexRow>
                      <FormattedMessage defaultMessage="Subject" id="LLtKhp" />
                      <Asterisk />
                    </FlexRow>
                    <Field name="subjectTitle">
                      {({ input, meta: { error, touched } }) => (
                        <>
                          <Input
                            {...input}
                            className={error && touched ? "invalid" : ""}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Enter your Subject",
                              id: "9PXY25",
                            })}
                            required
                          />
                          {error && touched && (
                            <RequiredSpan>{error}</RequiredSpan>
                          )}
                        </>
                      )}
                    </Field>
                  </Label>
                  <Label>
                    <FlexRow>
                      <FormattedMessage
                        defaultMessage="Message Body"
                        id="IrTeKe"
                      />
                      <Asterisk />
                    </FlexRow>
                    <Field name="subjectBody">
                      {({ input, meta: { error, touched } }) => (
                        <>
                          <TextArea
                            {...input}
                            className={error && touched ? "invalid" : ""}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Your message here...",
                              id: "nmmuZH",
                            })}
                            required
                          />
                          {error && touched && (
                            <RequiredSpan>{error}</RequiredSpan>
                          )}
                        </>
                      )}
                    </Field>
                  </Label>
                  <div className="flex flex-col gap-2 justify-end">
                    <PrimaryButton
                      type="submit"
                      style={{ marginTop: "1.5rem" }}
                      fullWidth
                      isLoading={submitting}
                      disabled={
                        Object.keys(values).length !== 5 ||
                        !!Object.keys(errors!).length ||
                        submitting
                      }
                    >
                      <FormattedMessage defaultMessage="Send" id="9WRlF4" />
                    </PrimaryButton>
                    {isSuccess === true ? (
                      <SuccessMessage />
                    ) : isSuccess === false ? (
                      <FailureMessage />
                    ) : null}
                  </div>
                </form>
              );
            }}
          />
        </ContactForm>
        <ContactInfo>
          {isContactInfoExist && (
            <ContactInfoWrapper>
              <AddressBook alignItems="flex-start">
                {email && (
                  <InfoHolder>
                    <MailIcon />
                    <Info alignItems="flex-start" spacing="s">
                      <ContactInfoHeader>
                        <FormattedMessage defaultMessage="Email" id="sy+pv5" />
                      </ContactInfoHeader>
                      <ContactInfoValue>{email}</ContactInfoValue>
                    </Info>
                  </InfoHolder>
                )}
                {phone && (
                  <InfoHolder>
                    <PhoneIcon />
                    <Info alignItems="flex-start" spacing="s">
                      <ContactInfoHeader>
                        <FormattedMessage defaultMessage="Phone" id="O95R3Z" />
                      </ContactInfoHeader>
                      <ContactInfoValue>{phone}</ContactInfoValue>
                    </Info>
                  </InfoHolder>
                )}
                {address && (
                  <InfoHolder>
                    <AddressIcon />
                    <Info alignItems="flex-start" spacing="s">
                      <ContactInfoHeader>
                        <FormattedMessage
                          defaultMessage="Address"
                          id="e6Ph5+"
                        />
                      </ContactInfoHeader>
                      <ContactInfoValue>{address}</ContactInfoValue>
                    </Info>
                  </InfoHolder>
                )}
              </AddressBook>
            </ContactInfoWrapper>
          )}
          {socialLinkContent}
        </ContactInfo>
      </StyledSection>
    </Container>
  );
};

const validate = (values: ContactFormValues) => {
  const errors: Record<string, unknown> = {};

  if (!values.name?.trim()) {
    errors.name = (
      <FormattedMessage defaultMessage="Enter your full name" id="xIDmuE" />
    );
  }

  if (
    !values?.email ||
    !values?.email?.match(
      /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/
    )
  ) {
    errors.email = (
      <FormattedMessage
        defaultMessage="Enter a valid email address"
        id="YRe93Y"
      />
    );
  }

  if (!values.phone?.isValid) {
    errors.phone = (
      <FormattedMessage
        defaultMessage="Enter a valid phone number"
        id="iydw3u"
      />
    );
  }

  if (!values.subjectTitle?.trim()) {
    errors.subjectTitle = (
      <FormattedMessage defaultMessage="Enter subject title" id="KkreSs" />
    );
  }

  if (!values.subjectBody?.trim()) {
    errors.subjectBody = (
      <FormattedMessage defaultMessage="Enter subject content" id="ND8eVa" />
    );
  }
  return errors;
};

export default Contact;
